import { Button, ButtonVariants, getTargetTypeDisplayName, GroundTruthCounter, Icons, TargetTypes } from "@app/shared";
import { ChangeEventHandler, useState } from "react";
import { CheckboxInput, NumericInput, Select } from "../../../shared";
import { GroundTruthCounterOption } from "@app/shared/src/models";
import { Tab, TabContainer } from "../../../shared/TabContainer";
import { DateInput } from "../../../shared/inputs/DateInput";
import { FlagEnumCheckboxInput } from "../../../shared/inputs/FlagEnumCheckboxInput";

export type GroundTruthEntryMode = "count" | "photos";

type GroundTruthEntryFormProps = {
    /**
     * TabContainer owns which tab is active, so this reports the change rather than being driven by
     * a `mode` prop — the parent tracks it only to know which way to submit.
     */
    onModeChange: (mode: GroundTruthEntryMode) => void;
    count: number | undefined;
    onCountChange: (count: number | undefined) => void;
    selectedFiles: File[];
    onFilesChange: (files: File[]) => void;
    asOfDate: Date | null;
    onDateChange: (date: Date | null) => void;
    facilityTimezone?: string;
    /** Short zone name for the facility ("MDT") — the as-of time is entered in the site's zone, not the browser's. */
    timeZoneLabel: string;
    /** Named in the copy, so it's clear whose triggers are being borrowed. */
    facilityName: string;
    /** What this location's event triggers filter on; 0 when none. */
    facilityTargetFilter: number;
    targetFilter: number;
    onTargetFilterChange: (filter: number) => void;
    /**
     * Counters this user may pick between, from the server. Empty or undefined for everyone but a
     * superadmin, which also hides the picker — an ordinary upload always uses the configured
     * default, and offering a choice that costs money per run is not an ordinary decision.
     */
    counterOptions?: GroundTruthCounterOption[];
    counter: GroundTruthCounter;
    onCounterChange: (counter: GroundTruthCounter) => void;
};

/**
 * A 14-digit yyyyMMddHHmmss run anywhere in a filename — this camera's naming convention
 * ("20260904090152_TPAS Eastbound Lot.jpg"), and the same pattern GroundTruthController falls back
 * to server-side.
 */
const FILENAME_TIMESTAMP = /(?<!\d)(\d{14})(?!\d)/;

function timestampFromFilename(name: string): Date | undefined {
    const match = FILENAME_TIMESTAMP.exec(name);
    if (!match) return undefined;
    const [, digits] = match;
    const parsed = new Date(
        Number(digits.slice(0, 4)), Number(digits.slice(4, 6)) - 1, Number(digits.slice(6, 8)),
        Number(digits.slice(8, 10)), Number(digits.slice(10, 12)), Number(digits.slice(12, 14)));
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** The most recent timestamp derivable from a batch's filenames, if any carry one. */
export function latestTimestampFromFiles(files: File[]): Date | undefined {
    const stamps = files.map(f => timestampFromFilename(f.name)).filter((d): d is Date => !!d);
    if (!stamps.length) return undefined;
    return stamps.reduce((latest, d) => (d > latest ? d : latest));
}

/**
 * One dialog for both ways of establishing ground truth: type the count, or upload photos and let
 * the model count them. They produce the same thing — a known total at a known moment — so
 * splitting them across two entry points only made the user pick a mechanism before a goal.
 */
export const GroundTruthEntryForm: React.FC<GroundTruthEntryFormProps> = ({
    onModeChange, count, onCountChange, selectedFiles, onFilesChange,
    asOfDate, onDateChange, facilityTimezone, timeZoneLabel, facilityName,
    facilityTargetFilter, targetFilter, onTargetFilterChange,
    counterOptions, counter, onCounterChange,
}) => {
    const handleFileInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const files: File[] = [];
        for (let i = 0; i < (e.target.files?.length ?? 0); i++) {
            const file = e.target.files?.item(i);
            if (file) files.push(file);
        }
        onFilesChange(files);

        // The photos already say when they were taken, and that moment — not now — is what the
        // count is true at. Latest of the batch: the set is captured over a short sweep, and the
        // last frame is the closest thing to "when this was the state of the lot".
        const latest = latestTimestampFromFiles(files);
        if (latest) onDateChange(latest);

        // Reset so picking the same files again still fires a change event.
        e.target.value = "";
    };

    const removeFile = (index: number) => onFilesChange(selectedFiles.filter((_, i) => i !== index));

    return (
        <div className="flex flex-col gap-4 w-full min-w-[28rem]">
            {/* The app's own tab component rather than a bespoke segmented control — same
                interaction and underline as every other tabbed surface here. */}
            <TabContainer onTabChange={(title) => onModeChange(title === "Enter a count" ? "count" : "photos")}>
                <Tab title="Enter a count" className="pt-4">
                    <NumericInput
                        label="Vehicles on site"
                        value={count}
                        onChange={onCountChange}
                        min={0}
                    />
                    <TargetTypeSection
                        heading="What this count represents"
                        description="Which target types you counted. The live total is only comparable to a count of the same things."
                        facilityName={facilityName}
                        facilityTargetFilter={facilityTargetFilter}
                        targetFilter={targetFilter}
                        onTargetFilterChange={onTargetFilterChange}
                    />
                </Tab>

                <Tab title="Upload photos" className="pt-4">
                    <label className="block mb-1 font-semibold">Photos</label>
                    <input type="file" accept="image/*" multiple onChange={handleFileInputChange} />
                    <p className="text-sm text-semi-bright-text mt-1">
                        Select every photo for this set. Order doesn't matter — the stitcher works out
                        how they overlap from the images themselves.
                    </p>
                    {selectedFiles.length > 0 &&
                        <div className="flex flex-col gap-1 mt-2 max-h-48 overflow-y-auto border border-separators-page rounded p-2">
                            {selectedFiles.map((file, idx) => (
                                <div key={`${file.name}-${idx}`} className="flex items-center gap-2 text-sm">
                                    <span className="flex-grow truncate">{file.name}</span>
                                    <button type="button" className="px-1" onClick={() => removeFile(idx)} title="Remove">✕</button>
                                </div>
                            ))}
                        </div>
                    }
                    <TargetTypeSection
                        heading="What to count"
                        description="Which target types to count in these photos."
                        facilityName={facilityName}
                        facilityTargetFilter={facilityTargetFilter}
                        targetFilter={targetFilter}
                        onTargetFilterChange={onTargetFilterChange}
                    />
                    <CounterSection
                        options={counterOptions}
                        counter={counter}
                        onCounterChange={onCounterChange}
                    />
                </Tab>
            </TabContainer>

            <DateInput
                label={`As of (${timeZoneLabel})`}
                value={asOfDate ? asOfDate.toISOString() : undefined}
                onChange={onDateChange}
                displayTimezone={facilityTimezone}
                Icon={Icons.Calendar}
            />
            <p className="text-sm text-semi-bright-text -mt-2">
                The moment this count was true — not now, if the photos or the walk-through were earlier.
                Filled in from the photo filenames when they carry a timestamp.
            </p>
        </div>
    );
};

type CounterSectionProps = {
    options?: GroundTruthCounterOption[];
    counter: GroundTruthCounter;
    onCounterChange: (counter: GroundTruthCounter) => void;
};

/**
 * Which model counts this batch. Superadmin-only, and absent entirely otherwise — the server is the
 * gate (GroundTruthController.ParseCounter rejects the field for anyone else), this just avoids
 * offering a choice that would be refused.
 *
 * Every label comes from the server rather than being written here: what Groq or Claude would run
 * is configuration, and which build of the local detector is live is whatever weights the inference
 * service last loaded. A model name typed into this file would be right until either changed, and
 * then wrong in the one place someone looks to find out what ran.
 */
const CounterSection: React.FC<CounterSectionProps> = ({ options, counter, onCounterChange }) => {
    if (!options?.length) return null;

    return (
        <div className="mt-4">
            <Select
                label="Model"
                value={String(counter)}
                onChange={(e) => onCounterChange(Number(e.target.value) as GroundTruthCounter)}
                options={options.map(o => ({
                    value: String(o.counter),
                    // Labelled by the model, because that is the choice being made. The name falls
                    // in behind it only when the server has no model to report — for the local
                    // detector that means no run has fingerprinted its weights yet.
                    label: [o.model || o.name, o.isDefault ? "(default)" : ""].filter(Boolean).join(" "),
                }))}
            />
        </div>
    );
};

type TargetTypeSectionProps = {
    heading: string;
    description: string;
    facilityName: string;
    facilityTargetFilter: number;
    targetFilter: number;
    onTargetFilterChange: (filter: number) => void;
};

/**
 * Shared by both tabs, with wording that differs because the act differs: with photos we're being
 * told what to count, with a typed total we're being told what was already counted.
 */
const TargetTypeSection: React.FC<TargetTypeSectionProps> = ({
    heading, description, facilityName, facilityTargetFilter, targetFilter, onTargetFilterChange,
}) => {
    const [showTargetTypes, setShowTargetTypes] = useState(false);

    // 0 is the stored form of "no filter", but an unticked list reads as "nothing selected". Shown
    // as every box ticked, and collapsed back to 0 whenever the selection is once again everything,
    // so the boxes and the sentence above them always agree.
    const allSelectableTypes = Object.values(TargetTypes)
        .filter((v): v is number => typeof v === "number" && v !== TargetTypes.Unknown && v !== TargetTypes.Unspecified)
        .reduce((combined, value) => combined | value, 0);
    const shownFilter = targetFilter === 0 ? allSelectableTypes : targetFilter;
    const handleFilterChange = (value: number) =>
        onTargetFilterChange(value === allSelectableTypes ? 0 : value);

    return (
        <div className="mt-4">
            <label className="block mb-1 font-semibold">{heading}</label>
            <p className="text-sm text-semi-bright-text mb-2">
                {description}{" "}
                {targetFilter === 0
                    ? "Currently every vehicle type."
                    : facilityTargetFilter === targetFilter
                        ? `Currently the same target types ${facilityName}'s event triggers use.`
                        : "Currently a custom set."}
            </p>
            <CheckboxInput
                label="Choose target types"
                checked={showTargetTypes}
                onChange={() => setShowTargetTypes(!showTargetTypes)}
            />
            {showTargetTypes &&
                <div className="mt-2 border border-separators-page rounded p-2 max-h-48 overflow-y-auto">
                    <FlagEnumCheckboxInput
                        sort={true}
                        enumType={TargetTypes}
                        keyFilter={(key) => key !== TargetTypes.Unknown && key !== TargetTypes.Unspecified}
                        nameToDisplayConverter={getTargetTypeDisplayName}
                        value={shownFilter}
                        onChange={(val) => handleFilterChange(val ?? 0)}
                    />
                    <div className="flex gap-2 mt-3">
                        <Button variant={ButtonVariants.Primary} onClick={() => onTargetFilterChange(facilityTargetFilter)}>
                            Use {facilityName}'s triggers
                        </Button>
                        <Button variant={ButtonVariants.Primary} onClick={() => onTargetFilterChange(0)}>
                            Count everything
                        </Button>
                    </div>
                </div>
            }
        </div>
    );
};
